import { useCallback, useEffect, useState } from "react"

import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"
import { Button, Divider, Input, Message } from "semantic-ui-react"
import SearchParams from "simplydo/core/searchParams"

import actions from "actions"
import api from "api"
import TermsCheckbox from "components/lib/Entry/TermsCheckbox"
import { useAppDispatch, useAppSelector } from "store"
import constants from "utils/constants"

function Enrol() {
  const { t } = useTranslation()
  const [password, setPassword] = useState("")
  const [firstName, setFirstName] = useState("")
  const [invalidToken, setInvalidToken] = useState(false)
  const [loading, setLoading] = useState("")
  const [agreedTerms, setAgreedTerms] = useState(false)

  const email = useAppSelector((state) => state.entry.email)
  const error = useAppSelector((state) => state.entry.error)

  const organisation = useAppSelector((state) => state.organisations.urlOrganisation)

  const dispatch = useAppDispatch()
  const setEmail = useCallback((e) => dispatch(actions.entry.updateEmail(e)), [dispatch])
  const setError = useCallback((e) => dispatch(actions.entry.updateError(e)), [dispatch])

  const [agreedCustomTerms, setAgreedCustomTerms] = useState(!organisation?.customTerms?.enabled)
  const navigate = useNavigate()

  useEffect(() => {
    const params = new SearchParams(window.location.search)
    const token = params.get("token")
    if (token) {
      try {
        const base64Url = token.split(".")[1]
        if (!base64Url) {
          toast.error("Invalid enrolment token")
          setInvalidToken(true)
          return
        }
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
        const data = JSON.parse(window.atob(base64))
        if (data?.email && data.email !== email) {
          setEmail(data.email)
          setFirstName(data.given_name)
        }
      } catch (err) {
        toast.error("Invalid enrolment token")
        setInvalidToken(true)
      }
    }
  }, [email, setEmail])

  function completeSetPassword(callback) {
    const params = new SearchParams(window.location.search)
    const token = params.get("token")
    setLoading(true)
    setError("")
    api.auth.updatePasswordWithToken(
      token,
      password,
      () => {
        if (email) {
          api.auth.login(
            { email, password },
            () => {
              navigate("/")
              setTimeout(() => {
                callback()
              }, 50)
            },
            (err) => {
              setError(err.message)
              setLoading(false)
            },
          )
        } else {
          setLoading(false)
          navigate("/")
          toast(t("entry.enrol.passwordSuccess"))
        }
      },
      (err) => {
        setLoading(false)
        setError(err.message)
      },
    )
  }

  function agreeTerms() {
    setError("")
    api.auth.agreeToTerms(
      constants.termsVersion,
      organisation?.customTerms?.version,
      (agreements) => {
        setAgreedTerms(agreements)
        setLoading(false)
      },
      (err) => {
        setError(err.message)
        setLoading(false)
      },
    )
  }

  function clicked() {
    if (!agreedTerms) return setError(t("entry.enrol.agreeToTerms"))
    completeSetPassword(agreeTerms)
  }

  return (
    <div>
      {invalidToken ? (
        <>
          <h2>A problem occurred</h2>
          <Message negative>
            Your enrolment link is invalid or has expired. To receive a new enrolment link, try logging in again. Please
            get in touch with us if this issue persists.
            <br />
            <div style={{ marginTop: 15 }}>
              <Button primary as={Link} to="/login" content="Return to Login" />
            </div>
          </Message>
        </>
      ) : (
        <>
          <h2>{t("entry.enrol.welcome", { context: firstName || null })}</h2>
          <p>{t("entry.enrol.info")}</p>
          <Divider hidden />
          {error && <Message warning>{error}</Message>}
          <Input
            style={{ minHeight: "40px" }}
            fluid
            type="password"
            placeholder={t("entry.enrol.newPass")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          <Divider hidden />
          <TermsCheckbox
            organisation={organisation}
            agreed={agreedTerms}
            onAgree={(c) => setAgreedTerms(c)}
            agreedCustomTerms={agreedCustomTerms}
            onAgreeCustomTerms={(c) => setAgreedCustomTerms(c)}
          />
          <div style={{ marginTop: 30, textAlign: "right" }}>
            <Button primary content={t("entry.enrol.complete")} onClick={clicked} loading={loading} />
          </div>
        </>
      )}
    </div>
  )
}

export default Enrol
